// ---------------------------------------------------------------------------
// Pending transaction type
// ---------------------------------------------------------------------------

export interface PendingTransaction {
  transaction_id: string
  cart: Record<string, { key: string; name: string; price: number; quantity: number }>
  total: number
  phoneNumber: string | null
  status: 'pending' | 'paid' | 'printed' | 'sent' | 'completed'
  createdAt: string
  lastUpdate: string
}

// ---------------------------------------------------------------------------
// Recovery action type
// ---------------------------------------------------------------------------

export type RecoveryAction = 'retry_print' | 'retry_send' | 'cleanup'

// ---------------------------------------------------------------------------
// Transaction Recovery Service
// ---------------------------------------------------------------------------

class TransactionRecovery {
  private STORAGE_KEY = 'borne_current_transaction'
  private EXPIRY_MINUTES = 5

  /**
   * Save a pending transaction to localStorage.
   */
  save(tx: PendingTransaction): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(tx))
    } catch {
      // localStorage full or unavailable — silently fail
      console.warn('[TransactionRecovery] Failed to save transaction')
    }
  }

  /**
   * Load the pending transaction from localStorage.
   * Returns null if missing, expired (> 5 min), or malformed.
   */
  load(): PendingTransaction | null {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY)
      if (!raw) return null

      const tx = JSON.parse(raw) as PendingTransaction

      // Check expiry
      const lastUpdate = new Date(tx.lastUpdate).getTime()
      const now = Date.now()
      const expiryMs = this.EXPIRY_MINUTES * 60 * 1000

      if (now - lastUpdate > expiryMs) {
        this.clear()
        return null
      }

      return tx
    } catch {
      this.clear()
      return null
    }
  }

  /**
   * Update the status and lastUpdate timestamp of the saved transaction.
   */
  updateStatus(status: PendingTransaction['status']): void {
    const tx = this.load()
    if (!tx) return

    tx.status = status
    tx.lastUpdate = new Date().toISOString()
    this.save(tx)
  }

  /**
   * Clear the saved transaction from localStorage.
   */
  clear(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY)
    } catch {
      // Silently fail
    }
  }

  /**
   * Check if there is a recoverable (non-expired, non-completed) transaction.
   */
  hasRecoverableTransaction(): boolean {
    const tx = this.load()
    if (!tx) return false
    return tx.status !== 'completed'
  }

  /**
   * Determine the appropriate recovery action for a transaction.
   *
   * - pending: cleanup (never paid, too risky to retry payment)
   * - paid: retry_print (payment succeeded, need to print + send)
   * - printed: retry_send (printed OK, need to finalize)
   * - sent: cleanup (already sent, mark complete)
   * - completed: cleanup
   */
  getRecoveryAction(tx: PendingTransaction): RecoveryAction {
    switch (tx.status) {
      case 'paid':
        return 'retry_print'
      case 'printed':
        return 'retry_send'
      case 'pending':
      case 'sent':
      case 'completed':
      default:
        return 'cleanup'
    }
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const transactionRecovery = new TransactionRecovery()
